import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRecords } from '../services/InvoiceItemsService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export const useInvoiceItems = (invoiceIdProp?: string) => {
    const params = useParams();
    const invoiceId = invoiceIdProp ?? params.invoiceId ?? params.id;

    const queryClient = useQueryClient();
    const [limit] = useState(10);
    const [keyword, setKeyword] = useState('');

    const sort = { column: 'created', direction: 'desc' as const };

    const invoiceItemsQuery = useInfiniteQuery({
        queryKey: ['invoice-items', invoiceId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({
            limit,
            sort,
            nextToken: pageParam,
            filter: invoiceId ? { invoiceId } : undefined,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        enabled: !!invoiceId,
    });

    const fetchRecordsAction = () => {
        if (invoiceItemsQuery.hasNextPage) {
            invoiceItemsQuery.fetchNextPage();
        }
    };

    const reloadRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = invoiceItemsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: invoiceItemsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: invoiceItemsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reloadRecords,
        handleFilterByKeyword,
    };
};
